/**
 * admin.ts — lenient zod SHAPE gates for the admin-console READ/list endpoints (adminApi.ts).
 * Passed as fetchEnvelope's optional 3rd `schema` arg so a STRUCTURAL drift (array vs object, a
 * missing REQUIRED field, a wrong PRIMITIVE type) is caught at the API boundary as a typed
 * ApiShapeError instead of crashing deep in an admin table render.
 *
 * SCOPE: the READ/list shapes only (listTenants/listInstances/listUsers/getUser/listRoles/
 * listAuditLog). The mutate() POST/PATCH/DELETE responses are intentionally NOT gated this round.
 *
 * LENIENCY (from schemas/base.ts, followed EXACTLY): `| null` => `.nullable()`; optional `?` =>
 * `.optional()`; primitives only (z.string()/z.number()/z.boolean()); NO z.enum()/z.literal()
 * value-policing (broad unions like environment/status collapse to z.string()); NO .strict()
 * (extra server keys survive — the gate validates shape and returns the ORIGINAL payload).
 * connectionSecretRef is a POINTER (env-var name), never a secret — still just z.string().nullable().
 * AuditRow.metadata is `unknown` => z.unknown().
 *
 * Each exported list/detail schema carries an `extends` assertion proving the adminApi.ts CONTRACT
 * type is assignable to the schema's inferred type (the schema is never STRICTER than the contract,
 * so it can never false-positive-reject valid data). The reverse is intentionally not asserted
 * (primitives are deliberately wider than the contract's string-literal unions).
 */
import { z } from "zod";
import type {
  AdminTenant,
  AdminInstance,
  AdminUser,
  AdminUserDetail,
  UserRoleAssignment,
  AdminRole,
  AuditRow,
} from "../adminApi";
import { type AssertExtends } from "./base";

// ── Tenants ──────────────────────────────────────────────────────────────────
const tenantSchema = z.object({
  id: z.string(),
  slug: z.string(),
  displayName: z.string(),
  domain: z.string().nullable(),
  status: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export const adminTenantsSchema = z.array(tenantSchema) as unknown as z.ZodType<AdminTenant[]>;
type _TenantLink = AssertExtends<AdminTenant, z.infer<typeof tenantSchema>>;

// ── Instances ────────────────────────────────────────────────────────────────
const instanceSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  label: z.string(),
  // `"dev" | "prod"` union → plain string (rule 3, no value-policing).
  environment: z.string(),
  dbHost: z.string(),
  dbPort: z.number(),
  dbName: z.string(),
  dbUser: z.string(),
  lakebaseProjectId: z.string(),
  lakebaseBranch: z.string(),
  lakebaseEndpoint: z.string(),
  // POINTER to a secret-manager entry / env-var name, NEVER a secret value — still just a string.
  connectionSecretRef: z.string().nullable(),
  status: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export const adminInstancesSchema = z.array(instanceSchema) as unknown as z.ZodType<AdminInstance[]>;
type _InstanceLink = AssertExtends<AdminInstance, z.infer<typeof instanceSchema>>;

// ── Users + role assignments ─────────────────────────────────────────────────
const userSchema = z.object({
  id: z.string(),
  email: z.string(),
  fullName: z.string().nullable(),
  status: z.string(),
  azureB2cId: z.string().nullable(),
  lastInstanceId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export const adminUsersSchema = z.array(userSchema) as unknown as z.ZodType<AdminUser[]>;
type _UserLink = AssertExtends<AdminUser, z.infer<typeof userSchema>>;

const roleAssignmentSchema = z.object({
  id: z.string(),
  roleId: z.string(),
  roleName: z.string(),
  roleLabel: z.string(),
  tenantId: z.string().nullable(),
  instanceId: z.string().nullable(),
  createdAt: z.string(),
});
type _RoleAssignmentLink = AssertExtends<UserRoleAssignment, z.infer<typeof roleAssignmentSchema>>;

// AdminUserDetail = AdminUser & { roles: UserRoleAssignment[] } — the getUser detail shape.
const userDetailSchema = userSchema.extend({
  roles: z.array(roleAssignmentSchema),
});
export const adminUserDetailSchema = userDetailSchema as unknown as z.ZodType<AdminUserDetail>;
type _UserDetailLink = AssertExtends<AdminUserDetail, z.infer<typeof userDetailSchema>>;

// ── Roles (read-only) ────────────────────────────────────────────────────────
const roleSchema = z.object({
  id: z.string(),
  name: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  category: z.string(),
  vertical: z.string(),
  tenantId: z.string().nullable(),
  isSystem: z.boolean(),
  isHidden: z.boolean(),
  isImmutable: z.boolean(),
  permissions: z.array(z.string()),
});
export const adminRolesSchema = z.array(roleSchema) as unknown as z.ZodType<AdminRole[]>;
type _RoleLink = AssertExtends<AdminRole, z.infer<typeof roleSchema>>;

// ── Audit log (read-only, non-PHI) ───────────────────────────────────────────
const auditRowSchema = z.object({
  id: z.string(),
  actorEmail: z.string(),
  action: z.string(),
  resourceType: z.string().nullable(),
  resourceId: z.string().nullable(),
  tenantId: z.string().nullable(),
  instanceId: z.string().nullable(),
  traceId: z.string().nullable(),
  // Contract type is `unknown` — accept ANY JSON shape (rule: no value-policing on opaque metadata).
  metadata: z.unknown(),
  createdAt: z.string(),
});
export const adminAuditLogSchema = z.array(auditRowSchema) as unknown as z.ZodType<AuditRow[]>;
type _AuditRowLink = AssertExtends<AuditRow, z.infer<typeof auditRowSchema>>;
